import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { ApiError, setSessionExpiredHandler } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queries';
import { SplashScreen } from '@/components/layout/BrandLoader';
import { SiteProvider } from './site-context';

/**
 * Build the React Query client.
 *
 * Created inside a component via `useState` rather than at module scope. A
 * module-level client is shared across every render of the module, which in tests
 * means one test's cached data leaks into the next — a notorious source of tests that
 * pass alone and fail in sequence.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /*
          Thirty seconds, which is now about the client's own patience rather than about
          matching the server. It used to read "public content is cached by the API for a
          minute anyway", and that stopped being true when the public endpoints moved to
          `no-cache` — they revalidate on every use and answer with a 304, so a refetch
          here *can* return something newer. See PUBLIC_CONTENT_CACHE.
        */
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,

        /**
         * Retry transient failures only.
         *
         * Retrying a 401, 403 or 404 is pointless — the answer will not change — and
         * on a 401 it is actively harmful, because the client's own refresh-and-retry
         * has already run and further attempts only delay the redirect to sign-in.
         */
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500) {
            return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),

        refetchOnWindowFocus: false,
      },
      mutations: {
        // Never retry a mutation automatically: a POST that appeared to fail may have
        // succeeded, and a blind retry can duplicate a post or an invitation.
        retry: false,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }): ReactNode {
  const [queryClient] = useState(createQueryClient);

  /**
   * Register the client's session-expiry callback.
   *
   * In an effect, not in the render body: registering a handler is a side effect, and
   * doing it during render would re-register on every pass and break under React's
   * concurrent rendering.
   *
   * The handler only clears the cached session. It deliberately does not navigate —
   * `RequireAuth` already redirects whenever there is no session, so clearing the cache
   * is sufficient and keeps routing decisions inside the router rather than in a
   * module-level side channel.
   */
  useEffect(() => {
    setSessionExpiredHandler(() => {
      queryClient.setQueryData(queryKeys.session, null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.session });
    });
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <SiteProvider>
        {children}
        {/*
          An overlay, not a gate: the app mounts and fetches behind it, so the two seconds
          are spent on the brand rather than on the boot. Rendered last so it paints over
          whatever has already arrived.
        */}
        <SplashScreen />
        <Toaster position="bottom-right" toastOptions={{ className: 'text-sm' }} closeButton />
      </SiteProvider>
    </QueryClientProvider>
  );
}
